<template>
  <div class="hidden md:block">
    <div
      v-if="isPendingReviewsPerGrade"
      class="bg-surface-default-hi mt-16 w-full min-w-0 rounded-lg p-16 md:rounded-[32px] md:p-24"
      data-test="card-carousel-skeleton"
    >
      <RevSkeleton
        class="mb-16"
        height="24px"
        shape="rectangle"
        width="220px"
      />
      <div class="flex gap-8">
        <RevSkeleton
          v-for="n in 3"
          :key="n"
          height="130px"
          shape="rectangle"
          width="130px"
        />
      </div>
    </div>

    <ReviewPhotosGuidanceDrawer
      v-else-if="defaultImageUrl"
      :grades="gradesWithPictures"
      :initial-grade="selectedGradeLabel ?? gradesWithPictures[0]?.gradeLabel"
      :target-grade
      :target-picture-index
      :name="REVIEW_PHOTOS_GUIDANCE_DRAWER_NAME"
      :title="i18n(translations.drawerTitle)"
    >
      <template #trigger="{ open }">
        <div
          v-if="hasReviewsWithAttachments && reviewSlides.length > 0"
          class="bg-surface-default-hi mt-16 grid w-full min-w-0 overflow-hidden rounded-lg p-16 md:rounded-[32px] md:p-24"
        >
          <RevCardCarousel
            :center="false"
            desktop-card-width="narrow"
            mobile-card-width="narrow"
            class="w-full min-w-0 [&_ul]:!w-full"
            :next-alternative-text="i18n(translations.alternativeNext)"
            :prev-alternative-text="i18n(translations.alternativePrevious)"
            size="extra-small"
            :subtitle="i18n(translations.subtitle)"
            :title="carouselTitle"
          >
            <div
              v-for="(slide, position) in reviewSlides"
              :key="slide.id"
              class="h-full"
            >
              <RevCard
                class="h-full cursor-pointer overflow-hidden hover:drop-shadow-xl"
                @click="handleReviewClick(open, position + 1)"
              >
                <div
                  class="rounded-lg relative flex w-full aspect-square overflow-hidden"
                  data-test="review-photos-guidance-slide"
                >
                  <RevIllustration
                    :alt="slide.title"
                    class="rounded-lg h-full w-full object-cover"
                    :height="130"
                    :src="slide.imageUrl"
                    :width="130"
                  />

                  <div
                    class="rounded-lg absolute inset-0 flex flex-col justify-between p-8"
                  >
                    <RevTag
                      v-if="slide.customerName"
                      class="self-start"
                      :label="slide.customerName"
                      variant="secondary"
                    />
                    <span
                      v-if="slide.reviewDate"
                      class="text-static-light-hi body-2-bold [text-shadow:0_0_4px_rgba(0,0,0,0.5)]"
                    >
                      {{ slide.reviewDate }}
                    </span>
                  </div>
                </div>
              </RevCard>
            </div>
          </RevCardCarousel>
        </div>

        <div
          v-else-if="hasReviewsWithAttachments"
          class="bg-surface-default-hi mt-16 w-full min-w-0 rounded-lg p-16"
          data-test="review-photos-guidance-no-reviews-card"
        >
          <div class="flex flex-col gap-8">
            <p class="heading-2">{{ noReviewsCardTitle }}</p>
            <p class="text-static-default-low caption">
              {{ i18n(translations.noReviewsCardDescription) }}
            </p>
          </div>
        </div>
      </template>
    </ReviewPhotosGuidanceDrawer>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'
import { RevCardCarousel } from '@ds/components/CardCarousel'
import { RevIllustration } from '@ds/components/Illustration'
import { RevSkeleton } from '@ds/components/Skeleton'
import { RevTag } from '@ds/components/Tag'

import type { ReviewsPerGrade } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useFetchReviewsPerGrade/useFetchReviewsPerGrade.types'

import translations from '../../ReviewPhotosGuidance.translations'
import {
  REVIEW_PHOTOS_GUIDANCE_DRAWER_NAME,
  useReviewPhotosGuidance,
} from '../../composables/useReviewPhotosGuidance'
import ReviewPhotosGuidanceDrawer from '../ReviewPhotosGuidanceDrawer/ReviewPhotosGuidanceDrawer.vue'

const props = withDefaults(
  defineProps<{
    trackingModel?: string
    categoryId?: string
    reviewsPerGrade?: ReviewsPerGrade | null
    isPendingReviewsPerGrade?: boolean
  }>(),
  {
    trackingModel: '',
    categoryId: undefined,
    reviewsPerGrade: null,
    isPendingReviewsPerGrade: false,
  },
)

const i18n = useI18n()

const {
  targetGrade,
  targetPictureIndex,
  defaultImageUrl,
  gradesWithPictures,
  selectedGradeLabel,
  hasReviewsWithAttachments,
  reviewSlides,
  carouselTitle,
  noReviewsCardTitle,
  handleReviewClick,
} = useReviewPhotosGuidance(props)
</script>
